import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	useToast,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { FiX } from "react-icons/fi";
import * as yup from "yup";

import { Button } from "newui/components/Button";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel";
import { notifyCompanyAdmin } from "actions/RFQ/Supplier/rfqActions";
import { DocumentRequestEmailContent } from "./DocumentRequestEmailContent";

interface CompanyAdmin {
	id: number;
	name: string;
	email: string;
}

interface ContactAdminDrawerProps {
	admins: CompanyAdmin[];
	companyId: number;
	documentCategory: string;
	isOpen: boolean;
	projectName: string;
	requesterName: string;
	rfxId: number;
	rfxName: string;
	rfxType: string;
	onClose: () => void;
}

interface ContactAdminFormValues {
	adminId: string;
	message: string;
}

const validationSchema = yup.object().shape({
	adminId: yup.string().required("Please select an admin"),
	message: yup
		.string()
		.max(2000, "Message must be at most 2000 characters"),
});

const ContactAdminDrawer: React.FC<ContactAdminDrawerProps> = ({
	isOpen,
	onClose,
	admins,
	companyId,
	rfxId,
	rfxName,
	rfxType,
	projectName,
	documentCategory,
	requesterName,
}) => {
	const toast = useToast();

	const adminOptions = [...admins]
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((admin) => ({
			label: `${admin.name} (${admin.email})`,
			value: String(admin.id),
		}));

	const initialValues: ContactAdminFormValues = {
		adminId: adminOptions.length === 1 ? adminOptions[0].value : "",
		message: "",
	};

	const getSelectedAdmin = (adminId: string): CompanyAdmin | undefined => {
		return admins.find((a) => String(a.id) === adminId);
	};

	const handleSubmit = async (
		values: ContactAdminFormValues,
		{ setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
	) => {
		try {
			await notifyCompanyAdmin(companyId, {
				admin_id: Number(values.adminId),
				rfx_id: rfxId,
				document_category: documentCategory,
				message: values.message || null,
			});
			toast({
				title: "Notification sent",
				description: "Your company admin has been notified.",
				status: "success",
				duration: 4000,
				isClosable: true,
			});
			onClose();
		} catch {
			toast({
				title: "Error",
				description: "Failed to send notification. Please try again.",
				status: "error",
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Drawer
			placement="right"
			isOpen={isOpen}
			onClose={onClose}
			size="xl"
		>
			<DrawerOverlay onClick={onClose} />
			<DrawerContent maxWidth="1280px">
				<DrawerHeader className="border-b-2">
					<Flex
						justifyContent="space-between"
						align="center"
					>
						<div>
							<p className="text-xl font-semibold text-bg-brand-solid">
								Request Corporate Document
							</p>
							<p className="text-sm font-normal text-gray-500">
								Send a notification to your company admin requesting
								they upload a document for this {rfxType}.
							</p>
						</div>
						<button
							onClick={onClose}
							className="rounded-lg p-2 hover:bg-gray-100"
							aria-label="Close drawer"
							data-testid="close-contact-admin-drawer"
						>
							<FiX size={20} />
						</button>
					</Flex>
				</DrawerHeader>
				<DrawerBody>
					<Formik
						initialValues={initialValues}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
						validateOnMount={false}
					>
						{({ isSubmitting, isValid, dirty, values }) => {
							const selectedAdmin = getSelectedAdmin(values.adminId);

							return (
								<Form>
									<div className="grid grid-cols-3">
										<div className="col-span-1 pr-6">
											<p className="mb-4 text-lg font-semibold text-primary-900">
												Notification Details
											</p>
											<label className="mb-2 block text-sm font-medium text-gray-700">
												RFx: {rfxName}
											</label>
											<label className="mb-4 block text-sm text-gray-500">
												Category: {documentCategory}
											</label>
											<Field name="adminId">
												{({
													field,
												}: {
													field: { name: string; value: string };
												}) => {
													const { ...fieldProps } = field;
													return (
														<FormikInputWithLabel
															label="Select Admin"
															{...fieldProps}
															type="select"
															options={adminOptions}
															placeholder="Choose a company admin..."
														/>
													);
												}}
											</Field>
											<Field name="message">
												{({
													field,
												}: {
													field: React.InputHTMLAttributes<HTMLTextAreaElement> & {
														name: string;
													};
												}) => {
													const {
														size: _size,
														...fieldProps
													} = field;
													return (
														<FormikInputWithLabel
															label="Additional Message (Optional)"
															{...fieldProps}
															maxLength={2000}
															type="textarea"
															placeholder="Add any additional context about the document needed..."
														/>
													);
												}}
											</Field>
											<Flex
												gap={3}
												justify="flex-end"
												className="mt-4"
											>
												<Button
													btntype="secondary"
													onClick={onClose}
													disabled={isSubmitting}
													data-testid="cancel-contact-admin-btn"
												>
													Cancel
												</Button>
												<Button
													type="submit"
													disabled={
														isSubmitting ||
														!isValid ||
														!values.adminId
													}
													isLoading={isSubmitting}
													data-testid="send-contact-admin-btn"
												>
													Send Notification
												</Button>
											</Flex>
										</div>
										<div className="col-span-2">
											<p className="ml-10 px-11 text-lg font-semibold text-primary-900">
												Email Preview
											</p>
											<div className="ml-10 px-11">
												<DocumentRequestEmailContent
													adminName={
														selectedAdmin?.name ?? ""
													}
													requesterName={requesterName}
													rfxName={rfxName}
													rfxType={rfxType}
													projectName={projectName}
													documentCategory={
														documentCategory
													}
													additionalMessage={
														values.message
													}
												/>
											</div>
										</div>
									</div>
								</Form>
							);
						}}
					</Formik>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};

export { ContactAdminDrawer };
